/* Blok → HTML. Saf, fs'siz: üreticide ve panelin önizlemesinde aynı kod.

   Her blok bugünkü sitenin sınıflarıyla basılıyor (`section-title`,
   `section-lede`, `btn btn--primary`…), yani yeni bir görsel dil yok; bir
   başlık bloğu sitedeki başlık gibi görünüyor. Panelden eklenen genel
   bloklar için birkaç yeni sınıf style.css'in sonunda (`.b-*`).

   ── İki kip ────────────────────────────────────────────────

   Yayında blok yalnızca kendi işaretlemesini basıyor. Önizlemede
   (`ctx.edit`) iki ek öznitelik geliyor ve yerinde düzenleyici bunlarla
   çalışıyor:

     data-b="<blok kimliği>"       bloğun kökü
     data-f="<alan yolu>"          düzenlenebilir metin (`text`, `items.2.title`)

   Yayındaki HTML'de bu öznitelikler yok; golden test bunu da sınıyor.

   ── Dil ────────────────────────────────────────────────────

   TR metin HTML'e basılıyor; EN karşılık varsa `ctx.en`'e toplanıyor ve
   üretici onu `i18n.content.js` olarak yazıyor. Anahtar bloğun `key`'i
   (bugünkü sitenin anahtarı) ya da `b.<id>.<alan>`. */

import { renderComponent } from './components.ts';
import { escapeText, richToText, type Block, type Card, type Fact, type LinkButton, type ListItem, type Media, type Rich } from '@frawgraphy/shared';

export type PhotoInfo = { id: string; width: number; height: number; alt: string };

export type RenderCtx = {
  edit: boolean;
  /** EN karşılıklar — anahtar → temizlenmiş HTML. Oluşturucu doldurur. */
  en: Record<string, string>;
  /** Arşiv karesinin adresleri (üretici `mediaUrls` ile, önizleme R2 ile). */
  photo: (id: string) => { browse: string; full: string; info?: PhotoInfo } | null;
  /** Bileşen şablonları (templates/blocks/<tip>.html) — üretici diskten, panel paketinden verir. */
  templates?: Record<string, string>;
  partials?: Record<string, string>;
  /** Bileşenlerin sayfadan aldığı ortak değerler (Turnstile anahtarı gibi). */
  pageScope?: Record<string, unknown>;
  /**
   * Ana sayfada görünen bölüm çapaları. Menü, gizlenmiş ya da silinmiş bir
   * bölüme giden `#çapa` bağlantısını basmıyor; yoksa bağlantı ölü kalırdı.
   */
  anchors?: Set<string>;
  /**
   * Yazı kipi: bloklar bir yazının `<article>`'ı içinde, eski gövdelerin
   * işaretlemesiyle basılıyor — sınıfsız başlık ve paragraf,
   * `article-list`, `article-quote` (style.css `.article …`).
   */
  prose?: boolean;
};

export function escapeAttr(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

/* ── Ortak parçalar ─────────────────────────────────────── */

/** Bloğun kök öznitelikleri: düzenleme işareti, stil kancası, görünürlük. */
export function rootAttrs(block: { id: string; style?: Block['style']; reveal?: boolean | undefined }, ctx: RenderCtx, extraClass = ''): string {
  const parts: string[] = [];
  const classes = [extraClass];
  if (block.reveal) classes.push('reveal');
  const s = block.style;
  if (s?.only === 'desktop') classes.push('b-only-desktop');
  if (s?.only === 'mobile') classes.push('b-only-mobile');
  if (s?.width) classes.push(`b-w-${s.width}`);
  if (s?.align) classes.push(`b-a-${s.align}`);
  const cls = classes.filter(Boolean).join(' ');
  if (cls) parts.push(`class="${cls}"`);
  if (hasCss(s)) parts.push(`data-bid="${escapeAttr(block.id)}"`);
  if (ctx.edit) parts.push(`data-b="${escapeAttr(block.id)}"`);
  return parts.length ? ` ${parts.join(' ')}` : '';
}

/** design.css'e kural düşen stil alanları; varsa blok `data-bid` taşır. */
function hasCss(s: Block['style']): boolean {
  return Boolean(s && (s.color || s.bg || s.scale || s.font || s.weight || s.padY || s.marginY));
}

/**
 * Metin alanının öznitelikleri ve içeriği. `htmlMode` bugünkü sitenin
 * davranışı: `data-i18n` metni düz değiştirir, `data-i18n-html` biçimi
 * korur. Biçim taşıyan bir metin her zaman `-html` ister — yoksa dil
 * değişiminde kalın yazı düz metne dönerdi.
 */
export function field(rich: Rich, key: string, path: string, ctx: RenderCtx, htmlMode: boolean): { attrs: string; html: string } {
  const html = rich.tr;
  const needsHtml = htmlMode || /<[a-z]/i.test(html) || /<[a-z]/i.test(rich.en ?? '');
  /* Yazılar yalnızca Türkçe yayımlanıyor: İngilizcesi girilmemiş bir yazı
     alanı i18n özniteliği taşımıyor (eski gövdeler de taşımıyordu). */
  if (ctx.prose && !rich.en) return { attrs: ctx.edit ? ` data-f="${escapeAttr(path)}"` : '', html };
  if (rich.en) ctx.en[key] = rich.en;
  const attrs = [`data-i18n${needsHtml ? '-html' : ''}="${escapeAttr(key)}"`];
  if (ctx.edit) attrs.push(`data-f="${escapeAttr(path)}"`);
  return { attrs: ` ${attrs.join(' ')}`, html };
}

/**
 * Alanın i18n anahtarı. Eski anahtarı olan blokta birincil alan (`text`)
 * anahtarın kendisini, diğer alanlar `<anahtar>.<alan>` alıyor — yoksa bir
 * alıntının metni ve kaynağı aynı sözlük girdisine düşerdi.
 */
const keyOf = (block: { id: string; key?: string | undefined }, name: string) =>
  block.key ? (name === 'text' ? block.key : `${block.key}.${name}`) : `b.${block.id}.${name}`;

export function mediaSrc(media: Media, ctx: RenderCtx): { src: string; full: string; width?: number; height?: number; alt: string } | null {
  if (media.photo) {
    const p = ctx.photo(media.photo);
    if (!p) return null;
    return {
      src: p.browse,
      full: p.full,
      ...(media.width ?? p.info?.width ? { width: media.width ?? p.info!.width } : {}),
      ...(media.height ?? p.info?.height ? { height: media.height ?? p.info!.height } : {}),
      alt: media.alt?.tr ?? escapeText(p.info?.alt ?? '')
    };
  }
  if (media.src) {
    return {
      src: media.src,
      full: media.src,
      ...(media.width ? { width: media.width } : {}),
      ...(media.height ? { height: media.height } : {}),
      alt: media.alt?.tr ?? ''
    };
  }
  return null;
}

function img(media: Media, ctx: RenderCtx, extra = ''): string {
  const m = mediaSrc(media, ctx);
  if (!m) return '';
  const size = m.width && m.height ? ` width="${m.width}" height="${m.height}"` : '';
  return `<img src="${escapeAttr(m.src)}" alt="${m.alt}"${size} loading="lazy" decoding="async"${extra}>`;
}

const ARROW = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5 12h14M13 6l6 6-6 6"/></svg>';

export function button(b: LinkButton, ctx: RenderCtx, path: string): string {
  const label = field(b.label, b.key ?? `b.${b.id}.label`, `${path}.label`, ctx, false);
  const edit = ctx.edit ? ` data-i="${escapeAttr(b.id)}"` : '';
  const cls = b.variant === 'plain' ? 'btn' : `btn btn--${b.variant}`;
  /* Sade düğme bugünkü 404'teki gibi her zaman `<span>` sarmalı taşıyor. */
  if (b.arrow || b.variant === 'plain') {
    return `<a class="${cls}" href="${escapeAttr(b.href)}"${edit}>\n<span${label.attrs}>${label.html}</span>${b.arrow ? `\n${ARROW}` : ''}\n</a>`;
  }
  return `<a class="${cls}" href="${escapeAttr(b.href)}"${edit}${label.attrs}>${label.html}</a>`;
}

/* ── Bloklar ────────────────────────────────────────────── */

export function renderBlocks(blocks: Block[], ctx: RenderCtx): string {
  return blocks
    .filter((b) => !b.style?.hidden)
    .map((b) => renderBlock(b, ctx))
    .join('\n');
}

export function renderBlock(block: Block, ctx: RenderCtx): string {
  switch (block.type) {
    case 'heading': {
      const f = field(block.text, keyOf(block, 'text'), 'text', ctx, true);
      const cls = ctx.prose ? '' : block.level === 1 ? 'page-title' : block.level === 2 ? 'section-title' : 'b-h' + block.level;
      return `<h${block.level}${rootAttrs(block, ctx, cls)}${f.attrs}>${f.html}</h${block.level}>`;
    }
    case 'paragraph': {
      const f = field(block.text, keyOf(block, 'text'), 'text', ctx, false);
      const cls = { lede: 'section-lede', lead: 'lede', body: '', small: 'b-small' }[block.variant ?? 'body'];
      return `<p${rootAttrs(block, ctx, cls)}${f.attrs}>${f.html}</p>`;
    }
    case 'eyebrow': {
      const f = field(block.text, keyOf(block, 'text'), 'text', ctx, false);
      return `<p${rootAttrs(block, ctx, 'section-index')}${f.attrs}>${f.html}</p>`;
    }
    case 'note': {
      const f = field(block.text, keyOf(block, 'text'), 'text', ctx, false);
      return (
        `<p${rootAttrs(block, ctx, block.variant === 'lang' ? 'lang-note' : 'disclosure')}>\n` +
        `<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 7.6v.9"/></svg>\n` +
        `<span${f.attrs}>${f.html}</span>\n</p>`
      );
    }
    case 'quote': {
      const f = field(block.text, keyOf(block, 'text'), 'text', ctx, true);
      const cite = block.cite ? field(block.cite, keyOf(block, 'cite'), 'cite', ctx, false) : null;
      if (ctx.prose) {
        /* Yazı alıntısı: eski gövdelerdeki `article-quote`; kaynak altında küçük satır. */
        return (
          `<blockquote${rootAttrs(block, ctx, 'article-quote')}${f.attrs}>${f.html}</blockquote>` +
          (cite ? `\n<p class="b-small"${cite.attrs}>${cite.html}</p>` : '')
        );
      }
      return (
        `<figure${rootAttrs(block, ctx, 'b-quote')}>\n<blockquote class="section-title"${f.attrs}>${f.html}</blockquote>` +
        (cite ? `\n<figcaption class="section-index"${cite.attrs}>${cite.html}</figcaption>` : '') +
        `\n</figure>`
      );
    }
    case 'list':
      return list(block, block.items, block.ordered, ctx, 'items', true);
    case 'table': {
      const rows = block.rows.map((row, r) => {
        const cells = row.map((cell, c) => {
          const tag = block.header && r === 0 ? 'th' : 'td';
          const f = field(cell, `${keyOf(block, 'rows')}.${r}.${c}`, `rows.${r}.${c}`, ctx, true);
          return `<${tag}${f.attrs}>${f.html}</${tag}>`;
        });
        return `<tr>${cells.join('')}</tr>`;
      });
      const head = block.header ? `<thead>${rows.shift()}</thead>` : '';
      return `<div${rootAttrs(block, ctx, 'b-table')}><table>${head}<tbody>${rows.join('')}</tbody></table></div>`;
    }
    case 'buttons': {
      const inner = block.items.map((b, i) => button(b, ctx, `items.${i}`)).join('\n');
      return `<div${rootAttrs(block, ctx, block.variant === 'more' ? 'work-more reveal' : 'b-buttons')}>\n${inner}\n</div>`;
    }
    case 'divider':
      return `<hr${rootAttrs(block, ctx, 'b-divider')}>`;
    case 'spacer':
      return `<div${rootAttrs(block, ctx, `b-spacer b-spacer--${block.size}`)} aria-hidden="true"></div>`;
    case 'image': {
      const ratio = block.ratio && block.ratio !== 'auto' ? ` style="aspect-ratio:${block.ratio.replace(':', ' / ')}"` : '';
      const cap = block.caption ? field(block.caption, keyOf(block, 'caption'), 'caption', ctx, true) : null;
      return (
        `<figure${rootAttrs(block, ctx, `b-image${block.bleed ? ' b-image--bleed' : ''}`)}>` +
        `<div class="b-image-frame"${ratio}>${img(block.media, ctx)}</div>` +
        (cap ? `<figcaption${cap.attrs}>${cap.html}</figcaption>` : '') +
        `</figure>`
      );
    }
    case 'gallery': {
      const items = block.items.map((m) => `<li${ctx.edit ? ` data-i="${escapeAttr(m.id)}"` : ''}>${img(m, ctx)}</li>`).join('');
      return `<ul${rootAttrs(block, ctx, `b-gallery b-gallery--${block.cols}`)}>${items}</ul>`;
    }
    case 'columns': {
      const cols = block.columns
        .map((c) => `<div class="b-col"${ctx.edit ? ` data-col="${escapeAttr(c.id)}"` : ''}>\n${renderBlocks(c.blocks, ctx)}\n</div>`)
        .join('\n');
      return `<div${rootAttrs(block, ctx, `b-columns b-columns--${block.columns.length}`)}>\n${cols}\n</div>`;
    }
    case 'cards':
      return block.variant === 'plain' ? plainCards(block, block.items, ctx) : renderComponent(block, ctx);
    case 'facts':
      return block.variant === 'plain' ? plainFacts(block, block.items, ctx) : renderComponent(block, ctx);
    case 'section':
      return section(block, ctx);
    case 'page': {
      /* Sayfa bölgesi: başlık kısmı `page-head` içinde, ardından bloklar. */
      const head = renderBlocks(block.head, ctx);
      const body = renderBlocks(block.blocks, ctx);
      return `<header class="page-head"${ctx.edit ? ` data-b="${escapeAttr(block.id)}"` : ''}>\n${head}\n</header>${body ? `\n${body}` : ''}`;
    }
    case 'html':
      return `<div${rootAttrs(block, ctx, 'b-html')}>${block.html}</div>`;
    default:
      return renderComponent(block, ctx);
  }
}

function list(block: Block & { id: string }, items: ListItem[], ordered: boolean, ctx: RenderCtx, path: string, root: boolean): string {
  const tag = ordered ? 'ol' : 'ul';
  const lis = items.map((item, i) => {
    const f = field(item.text, keyOf(block, `${path}.${i}`), `${path}.${i}.text`, ctx, true);
    const sub = item.items?.length ? list(block, item.items, ordered, ctx, `${path}.${i}.items`, false) : '';
    /* Yazıda alt listesi olmayan madde eski gövdeler gibi sarmalsız; alt
       liste varsa metin `<span>`'de — düzenlenen alan alt listeyi içine
       almasın. */
    if (ctx.prose && !sub) return `<li${f.attrs}>${f.html}</li>`;
    return `<li><span${f.attrs}>${f.html}</span>${sub}</li>`;
  });
  return `<${tag}${root ? rootAttrs(block, ctx, ctx.prose ? 'article-list' : 'b-list') : ''}>${lis.join('')}</${tag}>`;
}

function plainCards(block: Block & { id: string }, items: Card[], ctx: RenderCtx): string {
  const lis = items.map((card, i) => {
    const k = card.key ?? `b.${card.id}`;
    const title = field(card.title, `${k}.title`, `items.${i}.title`, ctx, true);
    const desc = card.desc ? field(card.desc, `${k}.desc`, `items.${i}.desc`, ctx, true) : null;
    const cat = card.category ? field(card.category, `${k}.cat`, `items.${i}.category`, ctx, false) : null;
    const media = card.media ? `<span class="card-media">${img(card.media, ctx)}</span>` : '';
    const meta = cat || card.year ? `<span class="card-meta">${cat ? `<span class="card-cat"${cat.attrs}>${cat.html}</span>` : ''}${card.year ? `<span class="card-year">${escapeText(card.year)}</span>` : ''}</span>` : '';
    const body = `${media}${meta}<h3 class="card-title"${title.attrs}>${title.html}</h3>${desc ? `<p class="card-desc"${desc.attrs}>${desc.html}</p>` : ''}`;
    const inner = card.href ? `<a href="${escapeAttr(card.href)}">${body}</a>` : body;
    return `<li class="card reveal"${ctx.edit ? ` data-i="${escapeAttr(card.id)}"` : ''}>${inner}</li>`;
  });
  return `<ul${rootAttrs(block, ctx, 'grid b-cards')}>${lis.join('')}</ul>`;
}

function plainFacts(block: Block & { id: string }, items: Fact[], ctx: RenderCtx): string {
  const rows = items.map((f, i) => {
    const k = f.key ?? `b.${f.id}`;
    const label = field(f.label, `${k}.label`, `items.${i}.label`, ctx, false);
    const value = field(f.value, `${k}.value`, `items.${i}.value`, ctx, true);
    return `<div${ctx.edit ? ` data-i="${escapeAttr(f.id)}"` : ''}><dt${label.attrs}>${label.html}</dt><dd${value.attrs}>${value.html}</dd></div>`;
  });
  return `<dl${rootAttrs(block, ctx, 'b-facts')}>${rows.join('')}</dl>`;
}

/**
 * Sayfa bölümü — bugünkü `<section class="section">` iskeleti. Başlık
 * blokları `section-head` içine, diğerleri altına. Başlık kimliği
 * (`<çapa>-title`) bölümün ilk ikinci düzey başlığına veriliyor;
 * `aria-labelledby` ona bağlı.
 */
function section(block: Extract<Block, { type: 'section' }>, ctx: RenderCtx): string {
  const titleId = `${block.anchor}-title`;
  const head = block.head.filter((b) => !b.style?.hidden);
  const hasTitle = head.some((b) => b.type === 'heading');
  const firstHeading = head.find((x) => x.type === 'heading');
  const headHtml = head
    .map((b) => {
      const html = renderBlock(b, ctx);
      /* Kimlik bugünkü sitedeki yerinde: `class`'tan hemen sonra. */
      return b === firstHeading ? html.replace(/^<h(\d)( class="[^"]*")?/, (_, n: string, cls = '') => `<h${n}${cls} id="${titleId}"`) : html;
    })
    .join('\n');
  const attrs = rootAttrs(block, ctx, `section${block.alt ? ' section--alt' : ''}`);
  return (
    `<section${attrs} id="${escapeAttr(block.anchor)}" data-section${hasTitle ? ` aria-labelledby="${titleId}"` : ''}>\n` +
    `<div class="shell">\n` +
    (head.length ? `<header class="section-head reveal">\n${headHtml}\n</header>\n` : '') +
    `${renderBlocks(block.blocks, ctx)}\n` +
    `</div>\n</section>`
  );
}

/** Bir sayfanın görünen metni — kelime sayısı, llms.txt gibi işler için. */
export function blocksText(blocks: Block[]): string {
  const out: string[] = [];
  const push = (r?: Rich) => r && out.push(richToText(r.tr));
  const visit = (b: Block) => {
    if ('text' in b && b.text) push(b.text as Rich);
    if (b.type === 'list') b.items.forEach(function walkItem(i: ListItem) { push(i.text); i.items?.forEach(walkItem); });
    if (b.type === 'table') b.rows.flat().forEach(push);
    if (b.type === 'cards') b.items.forEach((c) => { push(c.title); push(c.desc); });
    if (b.type === 'section') { b.head.forEach(visit); b.blocks.forEach(visit); }
    if (b.type === 'columns') b.columns.forEach((c) => c.blocks.forEach(visit));
    if (b.type === 'split') b.blocks.forEach(visit);
  };
  blocks.forEach(visit);
  return out.join(' ');
}
